import {
  ApiResponse,
  ChangePassPayload,
  CommandResponse,
  DeleteUserPayload,
  GetAddressesResponse,
  GetBleTokenResponse,
  GetHistoPayload,
  GetHistoResponse,
  GetInfosResponse,
  GetPoolsDetailResponse,
  GetWifiListResponse,
  GetWifiStatusResponse,
  ListPoolsResponse,
  RenameIOPayload,
  RenameIOResponse,
  SetAutoOff,
  SetInfosPayload,
  SetOutPayload,
  SetParamPayload,
  SetPlagesPayload,
  SetSeuilsPayload,
} from "@/models/api.models";
import sha1 from "sha1";
import { throwOnCommandError } from "@/shared/utils/commands";
import { getVersionId } from "@/shared/utils/misc/version";
import apiClient from "@/services/api.client.ts";
import { getBleClient } from "@/services/ble.client.ts";
import { computed } from "vue";
import { useBleDeviceId } from "@/features/ble/utils/useBleDeviceId.ts";
import { useStoredBlePool } from "@/features/ble/utils/localStorage.ts";

export const useApi = () => {
  console.log("useApi");
  const bleDeviceId = useBleDeviceId();
  const storedBlePool = useStoredBlePool(bleDeviceId);

  const client = computed(() =>
    bleDeviceId.value
      ? getBleClient(bleDeviceId.value, storedBlePool)
      : apiClient
  );

  return {
    async login(login: string, password: string): Promise<string> {
      return client.value
        .post(
          `/GetJWT.php`,
          {
            login,
            password: sha1(password),
            version: getVersionId(),
          },
          {
            withCredentials: true,
          }
        )
        .then((resp) => resp.headers.authorization.split(" ")[1]);
    },

    async listPools(
      max?: number,
      start?: number,
      search?: string,
      filter?: string,
      sort?: "p" | "P" | "a" | "A"
    ) {
      return client.value
        .post<ListPoolsResponse>("/GetIndex.php", {
          max,
          start,
          S: search,
          filter,
          sort,
        })
        .then((resp) => resp.data);
    },

    async getAddresses() {
      return client.value
        .post<GetAddressesResponse>("/GetAddresses.php", {})
        .then((resp) => resp.data);
    },

    async getPoolDetails(poolId: string) {
      console.log(bleDeviceId, client.value);
      return client.value
        .post<GetPoolsDetailResponse>("/GetPoolDetails.php", {
          poolID: poolId,
          lang: "fr",
        })
        .then((resp) => resp.data.response[0]);
    },

    async getInfos() {
      return client.value
        .get<GetInfosResponse>("/GetInfos.php")
        .then((resp) => resp.data.response.MyInfos);
    },

    async setInfos(payload: SetInfosPayload) {
      return client.value.post("/SetInfos.php", payload);
    },

    async changePass(payload: ChangePassPayload) {
      return client.value.post("/ChangePass.php", payload);
    },

    async setSeuils(payload: SetSeuilsPayload) {
      return client.value
        .post<CommandResponse>("/SetSeuils.php", payload)
        .then((resp) => resp.data.response[0].cmdID);
    },

    async setParam(payload: SetParamPayload) {
      return client.value
        .post<CommandResponse>("/SetParam.php", payload)
        .then((resp) => resp.data.response[0].cmdID);
    },

    async setOut(payload: SetOutPayload) {
      return client.value
        .post<CommandResponse>("/SetOut.php", payload)
        .then((resp) => resp.data.response[0].cmdID);
    },

    async setAutoOff(payload: SetAutoOff) {
      return client.value
        .post<CommandResponse>("/SetAutoOff.php", payload)
        .then((resp) => resp.data.response[0].cmdID);
    },

    async setPlages(payload: SetPlagesPayload) {
      return client.value
        .post<CommandResponse>("/SetPlages.php", payload)
        .then((resp) => resp.data.response[0].cmdID);
    },

    async waitCommand(cmdID: number) {
      return client.value.post("/WaitCommand.php", { cmdID }).then((resp) => {
        throwOnCommandError(resp.data.response.status);
        return resp;
      });
    },

    async getHisto(payload: GetHistoPayload) {
      return client.value
        .post<GetHistoResponse>("/GetHisto.php", payload)
        .then((resp) => resp.data.response);
    },

    async renameIO(payload: RenameIOPayload) {
      return client.value
        .post<RenameIOResponse>("/RenameIO.php", payload)
        .then((resp) => resp.data.response);
    },

    async deleteUser(payload: DeleteUserPayload) {
      return client.value
        .post<ApiResponse<unknown>>("/DeleteUser.php", {
          ...payload,
          pass: sha1(payload.pass),
        })
        .then((resp) => resp.data.response);
    },

    async getWifiList() {
      return client.value
        .post<GetWifiListResponse>("/ble:GetWifiList")
        .then((resp) => resp.data.response);
    },

    async getWifiStatus() {
      return client.value
        .post<GetWifiStatusResponse>("/ble:GetWifiStatus")
        .then((resp) => resp.data.response);
    },

    async getBleToken(payload: { phoneId: string; MAC: string }) {
      return client.value
        .post<GetBleTokenResponse>("/GetBleToken.php", payload)
        .then((resp) => resp.data.response);
    },

    async getRssi() {
      return client.value
        .post<GetBleTokenResponse>("/GetRssi.php")
        .then((resp) => resp.data.response);
    },

    async setWifi(payload: { ssid: string; password: string }) {
      return client.value
        .post("/ble:SetWifi", payload)
        .then((resp) => resp.data.response);
    },

    async clearWifi() {
      return client.value
        .post("/ble:ClearWifi")
        .then((resp) => resp.data.response);
    },
  };
};
